import React, { useEffect } from 'react';
import { Form, Field, useFormState } from 'react-final-form';
import { CheckboxField, ClassificatorField, TextField } from '../../atoms';
import { getLoadingSelector } from '../../utils/asyncHelpers';
import { Row, Col, Button, Spin, notification } from 'antd';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormActions } from '../../components';
import { FORM_CLASSIFIERS_EXTERNAL_REGISTRY_EDIT } from '../../constants/formKeys';
import {
  getClassifiersExternalRegistryChange,
  getClassifiersExternalRegistryItem,
  getClassifiersExternalRegistryItemData,
} from '../../reducers/classifiersReducer';
import * as actions from '../../actions/actionCreators/classifiersActionCreators';
import { externalRegistry } from '../../constants/parameters';
import validateSchema from './validateSchema';
import classifiersStore from '../../utils/classifiersStore';
import FormStateToRedux from '../FormStateToRedux';
import { makeRffValidateFromLegacy } from '../rffLegacyValidateAdapter';

// Inner form component that can access form state via useFormState
const ExternalRegistryFormFields = ({ t, operatorCode, onCancel }) => {
  const { values } = useFormState({ subscription: { values: true } });
  const isActive = !!values?.[externalRegistry.ACTIVE];

  return (
    <>
      <Row gutter={32}>
        <Col span={24}>
          <Field
            name={externalRegistry.CODE}
            label={t('fieldCode')}
            component={TextField}
            disabled={!!operatorCode}
            required
          />
        </Col>
      </Row>
      <Row gutter={32}>
        <Col span={12}>
          <Field
            name={externalRegistry.ENGLISH}
            label={t('fieldEn')}
            component={TextField}
            required
          />
        </Col>
        <Col span={12}>
          <Field
            name={externalRegistry.ESTONIAN}
            label={t('fieldEt')}
            component={TextField}
            required
          />
        </Col>
      </Row>
      <Row gutter={32}>
        <Col span={12}>
          <Field
            label={t('country')}
            name={externalRegistry.COUNTRY_CODE}
            component={ClassificatorField}
            classificatorType={ClassificatorField.types.countries}
            hasFeedback={true}
            required
            alphabetical
            optionFilter={(country) =>
              country.code?.length >= 2 && country.code?.length <= 3
            }
          />
        </Col>
        <Col span={12}>
          <Field
            name={externalRegistry.MEMBER_CODE}
            label={t('memberCode')}
            component={TextField}
            required={isActive}
          />
        </Col>
      </Row>
      <Row gutter={32}>
        <Col span={12}>
          <Field
            name={externalRegistry.ACTIVE}
            type="checkbox"
            component={CheckboxField}
            checkboxText={t('active')}
            hasFeedback={false}
            className="filter-checkbox"
          />
        </Col>
      </Row>
      <FormActions>
        <Button onClick={onCancel}>{t('buttonCancel')}</Button>
        <Button htmlType="submit" type="primary">
          {t('buttonSave')}
        </Button>
      </FormActions>
    </>
  );
};

ExternalRegistryFormFields.propTypes = {
  t: PropTypes.func.isRequired,
  operatorCode: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
};

const ExternalRegistryEdit = ({
  t,
  fetchClassifierExternalRegistry,
  changeClassifierExternalRegistry,
  fetchClassifierExternalRegistryList,
  isLoading,
  operatorCode,
  onCancel,
  onComplete,
  initialValues: initialValuesProp,
}) => {
  useEffect(() => {
    if (operatorCode) {
      fetchClassifierExternalRegistry(operatorCode);
    }
  }, [operatorCode, fetchClassifierExternalRegistry]);

  // Convert Immutable Map to plain JS
  const initialValues = initialValuesProp?.toJS
    ? initialValuesProp.toJS()
    : initialValuesProp || {
        [externalRegistry.CODE]: '',
        [externalRegistry.ENGLISH]: '',
        [externalRegistry.ESTONIAN]: '',
        [externalRegistry.COUNTRY_CODE]: '',
        [externalRegistry.MEMBER_CODE]: '',
        [externalRegistry.ACTIVE]: false,
      };

  const validate = makeRffValidateFromLegacy(
    validateSchema,
    { t },
    initialValues
  );

  const onSubmit = async (values) => {
    const result = await changeClassifierExternalRegistry(values);

    if (!result.error) {
      notification.info({
        message: t('completeTitle'),
        description: t('completeBody'),
      });

      onComplete();
    }

    await fetchClassifierExternalRegistryList();
    await classifiersStore.update();
  };

  return (
    <Spin spinning={isLoading}>
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validate}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <FormStateToRedux form={FORM_CLASSIFIERS_EXTERNAL_REGISTRY_EDIT} />
            <ExternalRegistryFormFields
              t={t}
              operatorCode={operatorCode}
              onCancel={onCancel}
            />
          </form>
        )}
      />
    </Spin>
  );
};

ExternalRegistryEdit.propTypes = {
  t: PropTypes.func.isRequired,
  fetchClassifierExternalRegistry: PropTypes.func.isRequired,
  changeClassifierExternalRegistry: PropTypes.func.isRequired,
  fetchClassifierExternalRegistryList: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  operatorCode: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
};

const getIsLoading = getLoadingSelector(
  getClassifiersExternalRegistryChange,
  getClassifiersExternalRegistryItem
);

const mapStateToProps = (state, ownProps) => ({
  initialValues: ownProps.operatorCode
    ? getClassifiersExternalRegistryItemData(state)
    : null,
  isLoading: getIsLoading(state),
});

const mapDispatchToProps = {
  fetchClassifierExternalRegistry: actions.fetchClassifierExternalRegistry,
  changeClassifierExternalRegistry: actions.changeClassifierExternalRegistry,
  fetchClassifierExternalRegistryList:
    actions.fetchClassifierExternalRegistryList,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExternalRegistryEdit);
